import React from 'react';
import type { Address } from './PersonSection';

interface PersonModalProps {
  mode: 'create' | 'edit';
  form: any;
  setForm: (f: any) => void;
  error: string | null;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  addresses: Address[];
}

export default function PersonModal({ mode, form, setForm, error, onSubmit, onCancel, addresses }: PersonModalProps) {

  // Helper to update a date entry
  const updateDate = (idx: number, field: string, value: string) => {
    setForm((f: any) => {
      const dates = [...(f.dates || [])];
      dates[idx] = { ...dates[idx], [field]: value };
      return { ...f, dates };
    });
  };
  // Helper to add a new date
  const addDate = () => {
    setForm((f: any) => ({ ...f, dates: [...(f.dates || []), { label: '', month: '', day: '', year: '' }] }));
  };
  // Helper to remove a date
  const removeDate = (idx: number) => {
    setForm((f: any) => {
      const dates = [...(f.dates || [])];
      dates.splice(idx, 1);
      return { ...f, dates };
    });
  };

  // Preferences editor helpers
  const updatePrefCategory = (cat: string, value: string) => {
    setForm((f: any) => {
      const prefs = { ...(f.preferences || {}) };
      // Don't split immediately - let the user type freely
      // Only split when we need to store the final value
      prefs[cat] = value;
      return { ...f, preferences: prefs };
    });
  };
  const addPrefCategory = () => {
    setForm((f: any) => {
      const prefs = { ...(f.preferences || {}) };
      // Use empty string as the new key so the user is prompted to enter a name
      prefs[''] = [];
      return { ...f, preferences: prefs };
    });
  };
  const removePrefCategory = (cat: string) => {
    setForm((f: any) => {
      const prefs = { ...(f.preferences || {}) };
      delete prefs[cat];
      return { ...f, preferences: prefs };
    });
  };

  return (
    <form role="form" onSubmit={onSubmit} style={{ background: '#fff', padding: 32, borderRadius: 12, minWidth: 320, maxWidth: 600, width: '100%', boxShadow: '0 2px 16px rgba(0,0,0,0.12)' }}>
      <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>{mode === 'create' ? 'New Person' : 'Edit Person'}</h3>
      {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
      <input placeholder="Name" value={form.name} onChange={e => setForm((f: any) => ({ ...f, name: e.target.value }))} required style={{ marginBottom: 10, width: '100%', padding: 8, boxSizing: 'border-box' }} />
      <input placeholder="Email (optional)" value={form.email} onChange={e => setForm((f: any) => ({ ...f, email: e.target.value }))} style={{ marginBottom: 10, width: '100%', padding: 8, boxSizing: 'border-box' }} />
      <input placeholder="Nickname (optional)" value={form.nickname} onChange={e => setForm((f: any) => ({ ...f, nickname: e.target.value }))} style={{ marginBottom: 10, width: '100%', padding: 8, boxSizing: 'border-box' }} />
      <input placeholder="Is a... (boy, girl, dog, teacher, etc.)" value={form.is_a || ''} onChange={e => setForm((f: any) => ({ ...f, is_a: e.target.value }))} style={{ marginBottom: 10, width: '100%', padding: 8, boxSizing: 'border-box' }} />
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Preferences</div>
        {Object.entries(form.preferences || {}).map(([cat, values]: [string, any]) => {
          const presetCats = ["likes", "dislikes"];
          const usedCats = Object.keys(form.preferences || {});
          const availablePresets = presetCats.filter(preset => preset === cat || !usedCats.includes(preset));
          const isPreset = presetCats.includes(cat);
          return (
            <div key={cat} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <select
                value={isPreset ? cat : 'custom'}
                onChange={e => {
                  const val = e.target.value;
                  if (presetCats.includes(val)) {
                    setForm((f: any) => {
                      const prefs = { ...(f.preferences || {}) };
                      prefs[val] = prefs[cat];
                      delete prefs[cat];
                      return { ...f, preferences: prefs };
                    });
                  } else if (val === 'custom') {
                    // Move to custom: set key to empty string and show input
                    setForm((f: any) => {
                      const prefs = { ...(f.preferences || {}) };
                      prefs[''] = prefs[cat];
                      delete prefs[cat];
                      return { ...f, preferences: prefs };
                    });
                  }
                }}
                style={{ flex: 1.5, minWidth: 0, padding: 8 }}
              >
                {availablePresets.map(preset => (
                  <option key={preset} value={preset}>{preset.charAt(0).toUpperCase() + preset.slice(1)}</option>
                ))}
                <option value="custom">Custom...</option>
              </select>
              {(!isPreset && cat === '') && (
                <input
                  placeholder="Custom category"
                  value=""
                  onChange={e => {
                    const newCat = e.target.value;
                    if (newCat.trim()) {
                      setForm((f: any) => {
                        const prefs = { ...(f.preferences || {}) };
                        prefs[newCat] = prefs[cat] || [];
                        delete prefs[cat];
                        return { ...f, preferences: prefs };
                      });
                    }
                  }}
                  style={{ flex: 1.5, minWidth: 0, padding: 8 }}
                />
              )}
              <input
                placeholder="Comma separated values"
                value={typeof values === 'string' ? values : (Array.isArray(values) ? values.join(', ') : '')}
                onChange={e => updatePrefCategory(cat, e.target.value)}
                style={{ flex: 3, minWidth: 0, padding: 8 }}
              />
              <button 
                type="button" 
                onClick={() => removePrefCategory(cat)} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: '#ef4444', 
                  fontWeight: 700, 
                  fontSize: 18, 
                  cursor: 'pointer' 
                }} 
                title="Remove"
              >
                ×
              </button>
            </div>
          );
        })}
        <button type="button" onClick={addPrefCategory} style={{ padding: '6px 12px', background: '#e5e7eb', color: '#2563eb', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 14, marginTop: 4 }}>Add Category</button>
      </div>
      {/* Important Dates Widget */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Important Dates</div>
        {(form.dates || []).map((date: any, idx: number) => {
          const presetLabels = ["Birthday", "Anniversary"];
          const usedLabels = (form.dates || []).map((d: any) => d.label).filter((l: string, i2: number) => l && i2 !== idx);
          const availablePresets = presetLabels.filter(preset => preset === date.label || !usedLabels.includes(preset));
          const isPreset = presetLabels.includes(date.label);
          return (
            <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <select
                value={isPreset ? date.label : 'custom'}
                onChange={e => {
                  const val = e.target.value;
                  if (presetLabels.includes(val)) {
                    updateDate(idx, 'label', val);
                  } else {
                    updateDate(idx, 'label', date.label && !presetLabels.includes(date.label) ? date.label : '');
                  }
                }}
                style={{ flex: 1.5, minWidth: 0, padding: 8 }}
              >
                {availablePresets.map(preset => (
                  <option key={preset} value={preset}>{preset}</option>
                ))}
                <option value="custom">Custom...</option>
              </select>
              {(!isPreset) && (
                <input
                  placeholder="Custom label"
                  value={date.label && !presetLabels.includes(date.label) ? date.label : ''}
                  onChange={e => updateDate(idx, 'label', e.target.value)}
                  style={{ flex: 1.5, minWidth: 0, padding: 8 }}
                />
              )}
              <select
                value={date.month || ''}
                onChange={e => updateDate(idx, 'month', e.target.value)}
                style={{ flex: 1, minWidth: 0, padding: 8 }}
              >
                <option value="">Month</option>
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
              <input
                type="number"
                min="1"
                max="31"
                placeholder="Day"
                value={date.day || ''}
                onChange={e => updateDate(idx, 'day', e.target.value)}
                style={{ flex: 1, minWidth: 0, padding: 8, boxSizing: 'border-box' }}
              />
              <input
                type="number"
                min="1900"
                max="2100"
                placeholder="Year"
                value={date.year || ''}
                onChange={e => updateDate(idx, 'year', e.target.value)}
                style={{ flex: 1, minWidth: 0, padding: 8, boxSizing: 'border-box' }}
              />
              <button type="button" onClick={() => removeDate(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', fontWeight: 700, fontSize: 18, cursor: 'pointer' }} title="Remove">×</button>
            </div>
          );
        })}
        <button type="button" onClick={addDate} style={{ padding: '6px 12px', background: '#e5e7eb', color: '#2563eb', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 14, marginTop: 4 }}>Add Date</button>
      </div>
      <select
        value={form.address === null ? '' : String(form.address)}
        onChange={e => setForm((f: any) => ({ ...f, address: e.target.value === '' ? null : Number(e.target.value) }))}
        style={{ marginBottom: 16, width: '100%', padding: 8, boxSizing: 'border-box' }}
      >
        <option value="">No Address</option>
        {addresses.map(addr => (
          <option key={addr.id} value={String(addr.id)}>
            {addr.nickname} — {addr.address_line1}, {addr.city} {addr.state} {addr.zip}
          </option>
        ))}
      </select>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        <button type="button" onClick={onCancel} style={{ padding: '8px 16px', background: '#e5e7eb', color: '#333', border: 'none', borderRadius: 6 }}>Cancel</button>
        <button type="submit" style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600 }}>{mode === 'create' ? 'Create' : 'Save'}</button>
      </div>
    </form>
  );
}
